/// <reference types="google.maps" />

import type { Renderer } from '@googlemaps/markerclusterer'
import type { MapLocatorClusteringSettings } from 'ecosystem'
import {
  DEFAULT_CLUSTER_COLOR,
  DEFAULT_CLUSTER_TEXT_COLOR,
  DEFAULT_MARKER_BORDER_COLOR,
  MAP_LOCATOR_CN,
  mapLocatorCn as cn
} from './constants'
import { markerSvg } from './markerSvg'

/** Digits stay legible as the badge grows, without the badge dominating the map. */
const scaleFor = (count: number, enabled?: boolean): number => {
  if (!enabled) {
    return 1
  }

  if (count >= 100) {
    return 1.35
  }

  if (count >= 25) {
    return 1.2
  }

  return count >= 10 ? 1.1 : 1
}

/**
 * Draws cluster badges as DOM so they inherit the store's font and can be
 * restyled from the widget's styling panel via `.map-locator__cluster`.
 *
 * The default renderer in @googlemaps/markerclusterer produces fixed blue
 * circles that ignore the widget's marker colours entirely.
 */
export const createClusterRenderer = (clustering: MapLocatorClusteringSettings): Renderer => {
  const color = clustering.color || DEFAULT_CLUSTER_COLOR
  const textColor = clustering.textColor || DEFAULT_CLUSTER_TEXT_COLOR
  const borderColor = clustering.borderColor || DEFAULT_MARKER_BORDER_COLOR
  const shape = clustering.shape ?? 'PIN'

  return {
    render: ({ count, position }) => {
      const element = document.createElement('div')
      const scale = scaleFor(count, clustering.scaleWithCount)

      element.className = cn(MAP_LOCATOR_CN.CLUSTER)
      element.innerHTML = markerSvg({
        fill: color,
        borderColor,
        borderWidth: clustering.borderWidth ?? 2,
        glyph: { type: 'count', value: count },
        shape
      })
      // The count is drawn in the border colour, so a separate text colour only
      // needs applying when the merchant has actually diverged from it
      if (textColor !== borderColor) {
        element.querySelector('text')?.setAttribute('fill', textColor)
      }

      element.style.transform = scale === 1 ? '' : `scale(${scale})`
      element.style.transformOrigin = shape === 'PIN' ? 'bottom center' : 'center'
      element.setAttribute('aria-label', `${count} locations`)

      return new google.maps.marker.AdvancedMarkerElement({
        position,
        content: element,
        // Keep clusters above individual markers so the count is never hidden
        zIndex: 1000 + count
      })
    }
  }
}
